/**
 * This is a set of helper functions to build treatments (aka, A/B tests and/or feature flags)
 * for our applications.
 *
 * ALL treatments must be registered at Split.io before they can be used AND included in the
 * treatments enum in the `constants/treatments.ts` file.
 */

import { SplitFactory } from '@splitsoftware/splitio'
import { TreatmentName } from '../constants/treatments'
import { TreatmentValue } from '../constants/treatmentValues'
import { v4 as uuidv4 } from 'uuid'
import { setRudderstackAnonymousId } from './logIdentity'
import Cookies from 'js-cookie'
import {
  IBrowserClient,
  Treatments,
} from '@splitsoftware/splitio/types/splitio'
let isAnonymousClientReady = false
let isUserClientReady = false

export function initializeSplitAnonymousClient(): void {
  /**
   * First, we get-or-set a UUID for our anonymous sessions that we will store on the localStorage
   * and will keep handy in the globalThis namespace. We will also set the rudderstack anonymousId to
   * be the same value.
   */
  const anonymousId = localStorage.getItem('anonymousId') || uuidv4()
  globalThis.anonymousId = anonymousId
  localStorage.setItem('anonymousId', anonymousId)
  const anonymousSplitFactory = SplitFactory({
    core: {
      authorizationKey:
        process.env.NEXT_PUBLIC_SPLIT_IO_KEY ||
        'j7ofd94c2124639n1jsc2dng9vo1hophc8vf',
      key: anonymousId, // This must be overwritten before we you use the client!!!
      trafficType: 'anonymous',
    },
    startup: {
      readyTimeout: 1.5, // seconds
    },
  })

  setRudderstackAnonymousId(anonymousId)
  Cookies.set('anonId', anonymousId)

  globalThis.anonymousSplitClient = anonymousSplitFactory.client()

  globalThis.anonymousSplitClient.on(
    globalThis.anonymousSplitClient.Event.SDK_READY,
    () => {
      isAnonymousClientReady = true
    }
  )
}

let userSplitClient: IBrowserClient

export function initializeSplitUserClient(userkey: string): void {
  /**
   * This client will be used for the user traffic type.
   *
   */
  const lowerCaseUserKey = userkey.toLowerCase()
  const userSplitFactory = SplitFactory({
    core: {
      authorizationKey:
        process.env.NEXT_PUBLIC_SPLIT_IO_KEY ||
        'j7ofd94c2124639n1jsc2dng9vo1hophc8vf',
      key: lowerCaseUserKey, // This is the key that you can use to identify the user in the split dashboard
      trafficType: 'user',
    },
    startup: {
      readyTimeout: 1.5, // seconds
    },
  })

  userSplitClient = userSplitFactory.client()
  globalThis.userSplitClient = userSplitClient

  globalThis.userSplitClient.on(
    globalThis.userSplitClient.Event.SDK_READY,
    () => {
      // eslint-disable-next-line
      isUserClientReady = true
    }
  )
}

/*
 T is the TreatmentName and is provided to TreatmentValue to auto-select
 the return type based on TreatmentName.
 Pass optional attributes parameter to handle treatments which need
 targeting with custom attributes
*/
export function getTreatment<T extends TreatmentName>(
  name: T,
  attributes?: SplitIO.Attributes
): TreatmentValue<T> {
  if (!isSplitReady()) {
    return 'control' as TreatmentValue<T>
  }

  return globalThis.anonymousSplitClient.getTreatment(name, attributes)
}

export function getUserTreatment<T extends TreatmentName>(
  name: T
): TreatmentValue<T> {
  if (!isUserSplitReady()) {
    return 'control' as TreatmentValue<T>
  }

  return globalThis.userSplitClient.getTreatment(name)
}

export function getUserTreatments(
  name: TreatmentName | Array<TreatmentName>
): Treatments | string {
  if (!isUserSplitReady()) {
    return 'control'
  }

  if (Array.isArray(name)) {
    return globalThis.userSplitClient.getTreatments(name)
  } else {
    return globalThis.userSplitClient.getTreatment(name)
  }
}

export function isSplitReady(): boolean {
  return !!globalThis.anonymousSplitClient && isAnonymousClientReady
}

export function isUserSplitReady(): boolean {
  return !!globalThis.userSplitClient && isUserClientReady
}

export { userSplitClient }
